import * as React from "react"

import * as PopoverPrimitive from "@radix-ui/react-popover"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../cn"

/* ── Variants ─────────────────────────────────────────────── */

const popoverContentVariants = cva([
  "z-50 rounded-md",
  "bg-surface-default text-coval-text",
  "border border-coval-border shadow-md",
  "p-3",
])

/* ── Root & Trigger ───────────────────────────────────────── */

const Popover = PopoverPrimitive.Root
const PopoverTrigger = PopoverPrimitive.Trigger
const PopoverClose = PopoverPrimitive.Close
const PopoverAnchor = PopoverPrimitive.Anchor

/* ── Content ──────────────────────────────────────────────── */

export type PopoverContentProps = React.ComponentPropsWithoutRef<
  typeof PopoverPrimitive.Content
> &
  VariantProps<typeof popoverContentVariants>

// Radix renders the content as an unnamed `role="dialog"`. The first
// PopoverTitle inside names it unless the caller passes `aria-label` or
// `aria-labelledby`.
interface PopoverTitleRegistry {
  register: (id: string) => () => void
}

const PopoverTitleContext = React.createContext<PopoverTitleRegistry | null>(
  null,
)

/**
 * Orders title ids as their elements appear in the document, so a title
 * inserted ahead of an existing one becomes the first and names the dialog.
 * Registration runs in an effect, after the titles are in the DOM.
 */
function inDocumentOrder(ids: string[]): string[] {
  return [...ids].sort((a, b) => {
    const first = document.getElementById(a)
    const second = document.getElementById(b)
    if (!first || !second) {
      return 0
    }
    return first.compareDocumentPosition(second) &
      Node.DOCUMENT_POSITION_FOLLOWING
      ? -1
      : 1
  })
}

const PopoverContent = React.forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(
  (
    {
      className,
      sideOffset = 4,
      align = "start",
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      ...props
    },
    ref,
  ) => {
    const [titleIds, setTitleIds] = React.useState<string[]>([])
    const registry = React.useMemo<PopoverTitleRegistry>(
      () => ({
        register: (id) => {
          setTitleIds((ids) => inDocumentOrder([...ids, id]))
          return () => setTitleIds((ids) => ids.filter((other) => other !== id))
        },
      }),
      [],
    )
    const labelledBy =
      ariaLabelledBy ?? (ariaLabel === undefined ? titleIds[0] : undefined)

    return (
      <PopoverPrimitive.Portal>
        <PopoverTitleContext.Provider value={registry}>
          <PopoverPrimitive.Content
            ref={ref}
            sideOffset={sideOffset}
            align={align}
            className={cn(popoverContentVariants(), className)}
            aria-label={ariaLabel}
            aria-labelledby={labelledBy}
            {...props}
          />
        </PopoverTitleContext.Provider>
      </PopoverPrimitive.Portal>
    )
  },
)

PopoverContent.displayName = PopoverPrimitive.Content.displayName

/* ── Title ────────────────────────────────────────────────── */

const PopoverTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, id, children, ...props }, ref) => {
  const generatedId = React.useId()
  const titleId = id ?? generatedId
  const registry = React.useContext(PopoverTitleContext)

  React.useEffect(() => registry?.register(titleId), [registry, titleId])

  return (
    <h3
      ref={ref}
      id={titleId}
      className={cn(
        "px-2 py-1.5 text-xs font-medium text-coval-text-muted",
        className,
      )}
      {...props}
    >
      {children}
    </h3>
  )
})

PopoverTitle.displayName = "PopoverTitle"

/* ── Item ─────────────────────────────────────────────────── */

export interface PopoverItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  checked?: boolean
}

const PopoverItem = React.forwardRef<HTMLButtonElement, PopoverItemProps>(
  ({ className, checked, children, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        "relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm py-1.5 px-2",
        "text-sm text-coval-text outline-none",
        "hover:bg-surface-default-hover disabled:hover:bg-transparent",
        "disabled:cursor-default disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
      {checked && (
        <span className="ml-auto flex h-3.5 w-3.5 shrink-0 items-center justify-center">
          <CheckIcon />
        </span>
      )}
    </button>
  ),
)

PopoverItem.displayName = "PopoverItem"

/* ── Inline icons ─────────────────────────────────────────── */

function CheckIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M2.5 6L5 8.5L9.5 3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/* ── Exports ──────────────────────────────────────────────── */

export {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverClose,
  PopoverAnchor,
  PopoverTitle,
  PopoverItem,
  popoverContentVariants,
}
